import * as React from "react";
import {useEffect, useMemo} from "react";
import {Accordion, Button, Header, Icon, Modal} from "semantic-ui-react";
import {FileViewer} from "./FileViewer";
import {backend} from "../../../xconvert-backend";
import {authentication} from "../../../authentication";
import {
    Attachment,
    Freight,
    StopLocation,
    Transport,
    TransportOrder,
    TransportOrderPathSelector
} from "../../../generated/api";
import {Splitter} from "../table-splitter-ui/src/components/Splitters/Splitter";

export interface FilesViewerModalProps {
    isOpen: boolean;
    onClose: () => void;
    order: TransportOrder
}

export class AttachmentWithPathSelector {
    attachment: Attachment
    pathSelector?: TransportOrderPathSelector
    stop?: StopLocation
    transport?: Transport
    freight?: Freight

    constructor(attachment: Attachment, pathSelector?: TransportOrderPathSelector, stop?: StopLocation, transport?: Transport, freight?: Freight) {
        this.attachment = attachment
        this.pathSelector = pathSelector
        this.stop = stop
        this.transport = transport
        this.freight = freight
    }
}

export async function getAttachmentInlineUrl(a?: AttachmentWithPathSelector) {
    let authHeader = backend.withTokenAuthHeader(authentication.token);

    if (a?.attachment.attachmentId && a.pathSelector?.transportOrderId) {
        let downloadUrl = await backend.attachmentApi.getAttachmentInlineUrl(
            a.attachment.attachmentId,
            a.pathSelector.transportOrderId,
            a.attachment.name,
            authHeader
        )
        return downloadUrl.preSignedUrl
    }
    return null;
}

type AttachmentLevel = "order" | "transport" | "freight" | "stop";

interface FileWithContext {
    attachment: Attachment;
    level: AttachmentLevel;
    transportId: string | null
    freightId: string | null
    stopId: string | null
}

interface SelectedFile {
    key: string
    name: string
    content: string
    url: string
}

interface AttachmentStats {
    totalAttachments: number;
    orderAttachments: number;
    freightAttachments: { freightId: string; count: number }[];
    transportAttachments: { transportId: string; count: number }[];
    stopAttachments: { stopId: string; transportId: string; count: number }[];
}

interface IdWithAttachmentCount {
    freightId?: string;
    transportId?: string;
    stopId?: string;
    count: number;
}

function fileKey(file: FileWithContext): string {
    const a = file.attachment;
    return [
        file.level,
        file.transportId ?? "",
        file.freightId ?? "",
        file.stopId ?? "",
        a?.attachmentId ?? "",
        a?.name ?? ""
    ].join("|");
}

function base64ToArrayBuffer(base64: string): ArrayBuffer {
    const binaryString = atob(base64);
    const len = binaryString.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) {
        bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes.buffer;
}

export function FilesViewerModal(props: FilesViewerModalProps) {
    const [selectedFile, setSelectedFile] = React.useState<SelectedFile | null>(null);
    const [loading, setLoading] = React.useState(false);
    const [inactiveAccordions, setInactiveAccordions] = React.useState<string[]>([]);
    const [attachmentStats, setAttachmentStats] = React.useState<AttachmentStats>({
        totalAttachments: 0,
        orderAttachments: 0,
        freightAttachments: [],
        transportAttachments: [],
        stopAttachments: []
    });

    const allFiles = useMemo<FileWithContext[]>(() => {
        const order = props.order;
        if (!order) return [];

        const files: FileWithContext[] = [];

        order.attachments?.forEach(a => files.push({
            attachment: a, level: "order", transportId: null, freightId: null, stopId: null
        }));

        order.freight?.forEach(f => f.attachments?.forEach(a => files.push({
            attachment: a, level: "freight", transportId: null, freightId: f.freightId, stopId: null
        })));

        order.transports?.forEach(t => {
            t.attachments?.forEach(a => files.push({
                attachment: a, level: "transport", transportId: t.transportId, freightId: null, stopId: null
            }));
            t.stops?.forEach(s => s.attachments?.forEach(a => files.push({
                attachment: a, level: "stop", transportId: t.transportId, freightId: null, stopId: s.stopId
            })));
        });

        return files;
    }, [props.order]);

    const selectedArrayBuffer = useMemo(() => {
        if (selectedFile?.content) {
            return base64ToArrayBuffer(selectedFile.content);
        }
        return null;
    }, [selectedFile?.content]);

    useEffect(() => {
        if (props.isOpen && props.order) {
            calculateAttachmentStats();
            const firstFile = allFiles[0] ?? null;
            if (firstFile) {
                selectFile(firstFile);
            } else {
                setSelectedFile(null);
            }
        } else {
            setSelectedFile(null);
        }
    }, [props.isOpen, props.order]);

    function calculateAttachmentStats() {
        let totalAttachments = props.order.attachments?.length || 0;
        const orderAttachments = props.order.attachments?.length || 0;
        const freightAttachments: { freightId: string; count: number }[] = [];

        props.order.freight?.forEach(f => {
            if (f.attachments && f.attachments.length > 0) {
                freightAttachments.push({freightId: f.freightId, count: f.attachments.length});
                totalAttachments += f.attachments.length;
            }
        });

        const transportAttachments: { transportId: string; count: number }[] = [];
        const stopAttachments: { stopId: string; transportId: string; count: number }[] = [];
        props.order.transports?.forEach(t => {
            if (t.attachments && t.attachments.length > 0) {
                transportAttachments.push({transportId: t.transportId, count: t.attachments.length});
                totalAttachments += t.attachments.length;
            }
            t.stops?.forEach(s => {
                if (s.attachments && s.attachments.length > 0) {
                    stopAttachments.push({stopId: s.stopId, transportId: t.transportId, count: s.attachments.length});
                    totalAttachments += s.attachments.length;
                }
            });
        });

        setAttachmentStats({
            totalAttachments,
            orderAttachments,
            freightAttachments,
            transportAttachments,
            stopAttachments
        });
    }

    function selectFile(file: FileWithContext) {
        const attachment = file.attachment;
        if (!attachment) return;

        const key = fileKey(file);

        if (selectedFile?.key === key) return;

        const inlineContent = attachment.uploadOnlyBase64AttachmentContent;

        // Case 1: attachment already carries its content inline.
        if (inlineContent) {
            setLoading(false);
            setSelectedFile({
                key,
                name: attachment.name || "Unknown File",
                content: inlineContent,
                url: ""
            });
            return;
        }

        setLoading(true);
        setSelectedFile({
            key,
            name: attachment.name || "Unknown File",
            content: "",
            url: ""
        });

        getAttachmentInlineUrl({
            attachment,
            pathSelector: {
                transportOrderId: props.order._id,
                transportId: file.transportId,
                freightId: file.freightId,
                stopId: file.stopId
            }
        }).then((url) => {
            setLoading(false);
            // Guard against a race: only apply if this file is still the selected one.
            setSelectedFile(prev => (prev && prev.key === key)
                ? {...prev, url: url || ""}
                : prev);
        }).catch((e) => {
            setLoading(false);
            console.error("Failed to load attachment url", e);
        });
    }

    function drawFileSelector() {
        function displayFile(file: FileWithContext) {
            const key = fileKey(file);
            return <Button
                key={key}
                primary={selectedFile?.key === key}
                style={{margin: "1px", height: "35px"}}
                onClick={() => selectFile(file)}
            >
                <span style={{
                    display: "inline-block",
                    maxWidth: "100%",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    verticalAlign: "middle"
                }}>
                    {file.attachment?.name || "Unknown File"}
                </span>
            </Button>;
        }

        function displayFiles(files: FileWithContext[]) {
            if (!files || files.length === 0) return <></>;
            return <div style={{display: 'flex', flexDirection: 'column', gap: '10px', padding: '10px'}}>
                {files.map(displayFile)}
            </div>;
        }

        function handleAccordionClick(name: string) {
            setInactiveAccordions(prev =>
                prev.includes(name) ? prev.filter(item => item !== name) : [...prev, name]
            );
        }

        function header(name: string, size: "h2" | "h3" | "h4" = "h2") {
            return <>
                <Icon name='dropdown'/>
                <Header as={size} style={{display: "inline-block", marginLeft: 8}}>{name}</Header>
            </>;
        }

        function accordionTitle(name: string, size: "h2" | "h3" | "h4" = "h2", nrOfAttachments = 0) {
            return <Accordion.Title
                active={!inactiveAccordions.includes(name)}
                index={0}
                onClick={() => handleAccordionClick(name)}
            >
                {header(`${name} (${nrOfAttachments} Files)`, size)}
            </Accordion.Title>;
        }

        const accordionStyle = {border: "1px solid #ccc", borderRadius: "6px"};

        function getTotal(items: IdWithAttachmentCount[]) {
            return items.map(f => f.count).reduce((a, b) => a + b, 0);
        }

        function getTransportAttachmentsCountWithStops(transportId: string) {
            const countFromStops = attachmentStats.stopAttachments
                .filter(s => s.transportId === transportId)
                .reduce((a, b) => a + b.count, 0);
            const countFromTransport = attachmentStats.transportAttachments
                .find(t => t.transportId === transportId)?.count || 0;
            return countFromStops + countFromTransport;
        }

        return <div style={{
            display: 'flex', flexDirection: 'column', overflowY: 'auto', width: '100%', maxHeight: '100%'
        }}>
            <Accordion styled fluid style={accordionStyle}>
                {accordionTitle("TransportOrder", "h2", attachmentStats.totalAttachments)}
                <Accordion.Content active={!inactiveAccordions.includes("TransportOrder")}>

                    <Accordion styled fluid style={accordionStyle}>
                        {accordionTitle("TransportOrder Attachments", "h3", attachmentStats.orderAttachments)}
                        <Accordion.Content active={!inactiveAccordions.includes("TransportOrder Attachments")}>
                            {displayFiles((props.order.attachments || []).map(a => ({
                                attachment: a, level: "order", transportId: null, freightId: null, stopId: null
                            })))}
                        </Accordion.Content>
                    </Accordion>

                    <Accordion styled fluid style={accordionStyle}>
                        {accordionTitle("Freight", "h2", getTotal(attachmentStats.freightAttachments))}
                        <Accordion.Content active={!inactiveAccordions.includes("Freight")}>
                            {props.order.freight?.map(f => {
                                if (!f.attachments || f.attachments.length === 0) return null;
                                return <React.Fragment key={f.freightId}>
                                    {displayFiles(f.attachments.map(a => ({
                                        attachment: a, level: "freight",
                                        transportId: null, freightId: f.freightId, stopId: null
                                    })))}
                                </React.Fragment>;
                            })}
                        </Accordion.Content>
                    </Accordion>

                    <Accordion styled fluid style={accordionStyle}>
                        {accordionTitle("Transports", "h2",
                            getTotal(attachmentStats.transportAttachments) + getTotal(attachmentStats.stopAttachments))}
                        <Accordion.Content active={!inactiveAccordions.includes("Transports")}>
                            {props.order.transports?.map(t => {
                                const hasTransportFiles = t.attachments && t.attachments.length > 0;
                                const hasStopFiles = t.stops?.some(s => s.attachments && s.attachments.length > 0);
                                if (!hasTransportFiles && !hasStopFiles) return null;

                                return <Accordion key={t.transportId} styled fluid style={accordionStyle}>
                                    {accordionTitle(`Transport ${t.transportId}`, "h3",
                                        getTransportAttachmentsCountWithStops(t.transportId))}
                                    <Accordion.Content active={!inactiveAccordions.includes(`Transport ${t.transportId}`)}>

                                        {hasTransportFiles && <Accordion styled fluid style={accordionStyle}>
                                            {accordionTitle(`Transport ${t.transportId} Files`, "h3",
                                                attachmentStats.transportAttachments.find(f => f.transportId === t.transportId)?.count || 0)}
                                            <Accordion.Content active={!inactiveAccordions.includes(`Transport ${t.transportId} Files`)}>
                                                {displayFiles((t.attachments || []).map(a => ({
                                                    attachment: a, level: "transport",
                                                    transportId: t.transportId, freightId: null, stopId: null
                                                })))}
                                            </Accordion.Content>
                                        </Accordion>}

                                        {hasStopFiles && <>
                                            <Header as="h4">Stops</Header>
                                            {t.stops?.map(s => {
                                                if (!s.attachments || s.attachments.length === 0) return null;
                                                return <Accordion key={s.stopId} styled fluid style={accordionStyle}>
                                                    {accordionTitle(`Stop ${s.stopId}`, "h4",
                                                        attachmentStats.stopAttachments
                                                            .filter(sa => sa.stopId === s.stopId)
                                                            .reduce((a, b) => a + b.count, 0))}
                                                    <Accordion.Content active={!inactiveAccordions.includes(`Stop ${s.stopId}`)}>
                                                        {displayFiles(s.attachments.map(a => ({
                                                            attachment: a, level: "stop",
                                                            transportId: t.transportId, freightId: null, stopId: s.stopId
                                                        })))}
                                                    </Accordion.Content>
                                                </Accordion>;
                                            })}
                                        </>}
                                    </Accordion.Content>
                                </Accordion>;
                            })}
                        </Accordion.Content>
                    </Accordion>

                </Accordion.Content>
            </Accordion>
        </div>;
    }

    function rightSide() {
        if (!selectedFile) return <p>No file selected</p>;

        if (selectedArrayBuffer) {
            return <div style={{display: 'flex', flexDirection: 'column', flex: 1, height: '100%', overflow: 'hidden'}}>
                <FileViewer
                    key={selectedFile.key}
                    fileBuffer={selectedArrayBuffer}
                    fileName={selectedFile.name}
                    displayName={false}
                />
            </div>;
        }

        if (loading) {
            return <div style={{flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center'}}>
                <p>Loading...</p>
            </div>;
        }

        if (selectedFile.url) {
            return <div style={{display: 'flex', flexDirection: 'column', flex: 1, height: '100%', overflow: 'hidden'}}>
                <iframe
                    id={"attachmentPreviewIFrame"}
                    key={selectedFile.key}
                    src={selectedFile.url}
                    title={selectedFile.name}
                    style={{width: '100%', height: '100%', border: 'none'}}
                />
            </div>;
        }

        return <p>No file selected or file content not available</p>;
    }

    return (
        <Modal open={props.isOpen} onClose={props.onClose} size="fullscreen"
               style={{height: '90vh', margin: '5vh auto'}}>
            <Modal.Header>Files Viewer</Modal.Header>
            <Modal.Content style={{
                display: 'flex', flexDirection: 'row', gap: '20px',
                overflow: 'hidden', height: 'calc(90vh - 100px)', padding: '1rem'
            }}>
                <Splitter
                    name={"FileViewerModalSplitter"}
                    left={<div style={{display: 'flex', flexDirection: 'column', height: '100%', overflowY: 'auto'}}>
                        {drawFileSelector()}
                    </div>}
                    right={rightSide()}
                />
            </Modal.Content>
        </Modal>
    );
}